'use client';

/**
 * Topbar search — suggestion dropdown.
 *
 * Behavior the user asked for:
 *   1. Click the input → dropdown opens with "Recent searches" + popular
 *      categories (instant, no network).
 *   2. Type a query → suggestions filter LOCALLY against AI models / tags /
 *      libraries / recent searches. The home gallery DOES NOT re-query the
 *      server on every keystroke — that was the previous behavior and it
 *      hammered the API.
 *   3. The actual gallery filter only fires when the user "finishes typing":
 *        - presses Enter
 *        - clicks a suggestion
 *        - stays idle for ~700ms (longer debounce than before)
 *   4. Esc closes the popover; click outside closes it; Tab from the input
 *      does not auto-commit.
 *
 * The popover is a fixed-position overlay anchored under the input via
 * getBoundingClientRect(), so it works regardless of the topbar's flex
 * layout. Recent searches persist in localStorage under `ps_recent_searches`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAIModels } from '@/lib/ai-models-context';
import { api } from '@/lib/api';

const RECENT_KEY = 'ps_recent_searches';
const RECENT_MAX = 6;
const SUGGESTIONS_PER_GROUP = 5;

interface Suggestion {
  kind: 'recent' | 'model' | 'tag' | 'library' | 'submit';
  label: string;
  /** Pretty subtitle shown right of the label in the dropdown. */
  hint?: string;
  /** Where clicking lands. Either a search string (driven through `?search=`)
   * or a direct path (used by tags / libraries that have dedicated routes). */
  href: string;
  /** What to record in the input box if the user picks this — usually equals
   * `label`, except for "Search for X" rows. */
  commitValue: string;
  icon: string;
}

function loadRecent(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function saveRecent(value: string) {
  if (!value || typeof window === 'undefined') return;
  const trimmed = value.trim();
  if (!trimmed) return;
  const list = loadRecent().filter((s) => s.toLowerCase() !== trimmed.toLowerCase());
  list.unshift(trimmed);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    /* swallow — quota or disabled */
  }
}

function clearRecent() {
  if (typeof window === 'undefined') return;
  try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ }
}


interface Props {
  inputRef: React.RefObject<HTMLInputElement | null>;
  /** Current value of the search input. */
  query: string;
  /** Update the input value (called when user picks a suggestion). */
  setQuery: (v: string) => void;
  /** Called when the user "commits" a search — Enter, suggestion click, or
   * the idle debounce fires. Passes the final query string + an optional
   * direct href (used by tag / library suggestions to bypass the search route). */
  onCommit: (query: string, directHref?: string) => void;
  /** Whether the dropdown is currently open. Parent controls so blurs/focus
   * sequencing stays in sync with the input. */
  open: boolean;
  setOpen: (v: boolean) => void;
}

export default function SearchSuggestions({
  inputRef, query, setQuery, onCommit, open, setOpen,
}: Props) {
  const router = useRouter();
  const { models } = useAIModels();
  const [tags, setTags] = useState<{ name: string; count: number }[]>([]);
  const [recents, setRecents] = useState<string[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Lazy fetch tags once when the dropdown first opens. Tags rarely change
  // and we don't need them before the user actually focuses the search.
  useEffect(() => {
    if (!open || tags.length > 0) return;
    api.tags.list()
      .then((rows) => setTags(rows.slice(0, 30)))  // cap to 30 — popularity-ordered
      .catch(() => { /* silent */ });
  }, [open, tags.length]);

  // Refresh recents when the popover opens (catches cross-tab updates).
  useEffect(() => {
    if (open) setRecents(loadRecent());
  }, [open]);

  // Position the popover under the input. Recompute on open + on window
  // resize so it doesn't drift off-screen.
  useEffect(() => {
    if (!open || !inputRef.current) {
      setPos(null);
      return;
    }
    const compute = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ top: r.bottom + 6, left: r.left, width: r.width });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, inputRef]);

  // Click outside closes the popover. Mousedown (not click) so it fires
  // before the input loses focus, avoiding flicker.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (inputRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open, setOpen, inputRef]);

  // Build the suggestion list. Pure function of query + sources.
  const suggestions = useMemo<Suggestion[]>(() => {
    const q = query.trim().toLowerCase();
    const out: Suggestion[] = [];

    // 1) Always-on top row: explicit "Search for X" so the user knows what
    //    Enter will do. Skip if empty (recent searches take its place).
    if (q) {
      out.push({
        kind: 'submit',
        label: `Search for "${query.trim()}"`,
        hint: 'Press Enter',
        href: `/?search=${encodeURIComponent(query.trim())}`,
        commitValue: query.trim(),
        icon: 'search',
      });
    }

    // 2) Recent searches — when input is empty, show all; when typing, only
    //    show recents that include the query.
    const recentMatches = recents.filter((r) => !q || r.toLowerCase().includes(q));
    for (const r of recentMatches.slice(0, SUGGESTIONS_PER_GROUP)) {
      out.push({
        kind: 'recent',
        label: r,
        hint: 'Recent',
        href: `/?search=${encodeURIComponent(r)}`,
        commitValue: r,
        icon: 'history',
      });
    }

    // 3) AI models — match name OR brand. Cap to a handful per dropdown.
    if (q) {
      const modelMatches = models
        .filter((m) => m.name.toLowerCase().includes(q) || m.brand.toLowerCase().includes(q))
        .slice(0, SUGGESTIONS_PER_GROUP);
      for (const m of modelMatches) {
        out.push({
          kind: 'model',
          label: m.name,
          hint: m.brand,
          href: `/?search=${encodeURIComponent(m.name)}`,
          commitValue: m.name,
          icon: 'smart_toy',
        });
      }

      // 4) Tags — match by tag name substring.
      const tagMatches = tags
        .filter((t) => t.name.toLowerCase().includes(q))
        .slice(0, SUGGESTIONS_PER_GROUP);
      for (const t of tagMatches) {
        out.push({
          kind: 'tag',
          label: `#${t.name}`,
          hint: `${t.count} prompt${t.count === 1 ? '' : 's'}`,
          // Tags have a dedicated filter route, so commit goes there directly.
          href: `/?tags=${encodeURIComponent(t.name)}`,
          commitValue: `#${t.name}`,
          icon: 'sell',
        });
      }
    }

    return out;
  }, [query, recents, models, tags]);

  // Reset highlight when suggestion list changes.
  useEffect(() => { setActiveIdx(0); }, [query, open]);

  if (!open || !pos) return null;

  // Empty-state when no query AND no recents — promote a hint to focus on
  // typing rather than rendering an empty box.
  const emptyHint = !query.trim() && recents.length === 0;

  const handlePick = (s: Suggestion) => {
    setQuery(s.commitValue);
    saveRecent(s.commitValue);
    setOpen(false);
    setRecents(loadRecent());
    if (s.href.startsWith('/?')) {
      onCommit(s.commitValue, s.href);
    } else {
      router.push(s.href);
    }
  };

  return (
    <div
      ref={containerRef}
      role="listbox"
      aria-label="Search suggestions"
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIdx((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && suggestions[activeIdx]) {
          e.preventDefault();
          handlePick(suggestions[activeIdx]);
        } else if (e.key === 'Escape') {
          setOpen(false);
        }
      }}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: pos.width,
        maxHeight: 'min(60vh, 480px)',
        overflowY: 'auto',
        background: 'var(--bg-raised)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        zIndex: 50,
        padding: 4,
      }}
    >
      {emptyHint ? (
        <div style={{
          padding: '14px 14px 12px',
          fontSize: 12,
          color: 'var(--ink-3)',
          lineHeight: 1.5,
        }}>
          Type to find prompts, models (e.g. <b>Claude</b>) or tags
          (e.g. <b>#fiction</b>). Suggestions appear here — pick one or press
          Enter to search.
        </div>
      ) : suggestions.length === 0 ? (
        <div style={{ padding: '14px 14px 12px', fontSize: 12, color: 'var(--ink-3)' }}>
          No suggestions for &ldquo;{query}&rdquo;. Press <b>Enter</b> to run a full search.
        </div>
      ) : (
        <>
          {suggestions.map((s, i) => (
            <button
              key={`${s.kind}:${s.label}:${i}`}
              type="button"
              role="option"
              aria-selected={activeIdx === i}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={(e) => {
                // Prevent the input from blurring before our click runs.
                e.preventDefault();
                handlePick(s);
              }}
              style={{
                width: '100%', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px',
                borderRadius: 'var(--radius-sm)',
                border: 0,
                background: activeIdx === i ? 'var(--bg-soft)' : 'transparent',
                color: 'var(--ink)',
                cursor: 'pointer',
                font: 'inherit',
                fontSize: 13,
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: 16,
                  color: s.kind === 'submit' ? 'var(--accent)' : 'var(--ink-3)',
                  flexShrink: 0,
                }}
              >
                {s.icon}
              </span>
              <span style={{
                flex: 1, minWidth: 0, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {s.label}
              </span>
              {s.hint && (
                <span style={{
                  fontSize: 11, color: 'var(--ink-3)', flexShrink: 0,
                }}>
                  {s.hint}
                </span>
              )}
            </button>
          ))}
          {recents.length > 0 && !query.trim() && (
            <div style={{
              borderTop: '1px solid var(--line-soft)',
              marginTop: 4, padding: '6px 10px',
              display: 'flex', justifyContent: 'flex-end',
            }}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  clearRecent();
                  setRecents([]);
                }}
                style={{
                  fontSize: 11, color: 'var(--ink-3)',
                  background: 'transparent', border: 0, cursor: 'pointer',
                }}
              >
                Clear recent
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export { saveRecent };
